<template>
	<div class="p-4 md:p-6 flex-1 overflow-y-auto font-sans">
		<!-- ── Section Header ── -->
		<div
			class="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 pb-5 border-b border-primary-100 dark:border-dark-600">
			<div class="flex items-center gap-3">
				<div
					class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
					<svg class="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
							d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
					</svg>
				</div>
				<div>
					<h1
						class="text-xl md:text-2xl font-extrabold font-display tracking-tight text-dark-700 dark:text-white">
						Overview
					</h1>
					<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
						Plan, prioritize, and accomplish your events with ease.
					</p>
				</div>
			</div>
		</div>

		<Breadcrumbs />

		<!-- ── Info Cards ── -->
		<!-- data-guide targets this entire stat grid -->
		<div data-guide="dashboard-stats"
			class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-6 mt-4">
			<InfoCard title="Number of Events" :value="eventCount" unit="Events" :trend="eventTrend"
				trendText="Last 7 days" :isLoading="isLoadingEvents" />
			<InfoCard title="Total Ticket Sold" :value="totalTicketsSold" unit="Ticket" :isLoading="isLoadingEvents" />
			<InfoCard title="Total Attendees" :value="attendeesCount" unit="Attendees" :isLoading="isLoadingEvents" />
			<InfoCard title="Revenue Generated" :value="totalRevenue" unit="€" :isLoading="isLoadingEvents" />
		</div>

		<!-- ── Charts + Event Status ── -->
		<!-- data-guide targets the analytics/chart area -->
		<div data-guide="analytics-section" class="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
			<!-- Revenue Trend Chart -->
			<div
				class="lg:col-span-2 bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 transition-colors duration-300">
				<Statistics :chartData="charts" timeRange="year" />
			</div>

			<!-- Event Status -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card p-6 border border-primary-100 dark:border-dark-600 transition-colors duration-300">
				<h2 class="font-display text-[15px] font-bold text-dark-700 dark:text-white mb-5">
					Event Status
				</h2>
				<div class="text-center mb-6">
					<div class="font-display text-4xl font-extrabold text-dark-700 dark:text-white leading-none">
						{{ eventCount }}
					</div>
					<div class="text-[12px] text-muted dark:text-muted-light mt-1.5">
						Total Events
					</div>
				</div>

				<div class="space-y-3.5">
					<!-- Active -->
					<div>
						<div class="flex items-center justify-between mb-1.5">
							<div class="flex items-center gap-2">
								<div class="w-2.5 h-2.5 rounded-full bg-primary-400 flex-shrink-0" />
								<span class="text-[13px] text-dark-400 dark:text-white/70">Active</span>
							</div>
							<span class="text-[13px] font-semibold text-dark-700 dark:text-white">{{ activeEventCount
							}}</span>
						</div>
						<div class="h-1.5 rounded-full bg-primary-50 dark:bg-dark-600 overflow-hidden">
							<div class="h-full rounded-full bg-primary-400 transition-all duration-700" :style="{
								width: eventCount
									? (activeEventCount / eventCount) * 100 + '%'
									: '0%',
							}" />
						</div>
					</div>

					<!-- Completed -->
					<div>
						<div class="flex items-center justify-between mb-1.5">
							<div class="flex items-center gap-2">
								<div class="w-2.5 h-2.5 rounded-full bg-primary-600 flex-shrink-0" />
								<span class="text-[13px] text-dark-400 dark:text-white/70">Completed</span>
							</div>
							<span class="text-[13px] font-semibold text-dark-700 dark:text-white">{{ completedEventCount
							}}</span>
						</div>
						<div class="h-1.5 rounded-full bg-primary-50 dark:bg-dark-600 overflow-hidden">
							<div class="h-full rounded-full bg-primary-600 transition-all duration-700" :style="{
								width: eventCount
									? (completedEventCount / eventCount) * 100 + '%'
									: '0%',
							}" />
						</div>
					</div>

					<!-- Cancelled -->
					<div>
						<div class="flex items-center justify-between mb-1.5">
							<div class="flex items-center gap-2">
								<div class="w-2.5 h-2.5 rounded-full bg-red-400 flex-shrink-0" />
								<span class="text-[13px] text-dark-400 dark:text-white/70">Cancelled</span>
							</div>
							<span class="text-[13px] font-semibold text-dark-700 dark:text-white">{{ canceledEventCount
							}}</span>
						</div>
						<div class="h-1.5 rounded-full bg-red-50 dark:bg-red-950/30 overflow-hidden">
							<div class="h-full rounded-full bg-red-400 transition-all duration-700" :style="{
								width: eventCount
									? (canceledEventCount / eventCount) * 100 + '%'
									: '0%',
							}" />
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- ── Top Events + Recent Activity ── -->
		<!-- data-guide targets the events table area -->
		<div data-guide="events-section" class="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
			<!-- Top Performing Events -->
			<div
				class="lg:col-span-2 bg-surface dark:bg-dark-700 rounded-card shadow-card p-6 border border-primary-100 dark:border-dark-600 transition-colors duration-300">
				<div class="flex items-center justify-between mb-5">
					<h2 class="font-display text-[15px] font-bold text-dark-700 dark:text-white">
						Top Performing Events
					</h2>
				</div>

				<div class="overflow-x-auto">
					<table class="w-full border-separate border-spacing-0">
						<thead>
							<tr>
								<th v-for="h in [
									'Event Name',
									'Tickets Sold',
									'Revenue',
									'Status',
								]" :key="h" class="text-[11px] font-bold uppercase tracking-widest pb-3 px-3 text-muted dark:text-muted-light border-b border-primary-100 dark:border-dark-600"
									:class="h === 'Event Name'
										? 'text-left'
										: h === 'Status'
											? 'text-center'
											: 'text-right'
										">
									{{ h }}
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="event in paginatedTopEvents" :key="event.name"
								class="group hover:bg-primary-50 dark:hover:bg-dark-600/50 transition-colors duration-150">
								<td
									class="py-3.5 px-3 text-[13px] font-medium text-dark-700 dark:text-white/90 border-b border-primary-50 dark:border-dark-600">
									{{ event.event_name }}
								</td>
								<td
									class="py-3.5 px-3 text-[13px] text-dark-400 dark:text-white/70 text-right border-b border-primary-50 dark:border-dark-600">
									{{ event.tickets_sold.toLocaleString() }}
								</td>
								<td
									class="py-3.5 px-3 text-[13px] font-semibold text-dark-700 dark:text-white text-right border-b border-primary-50 dark:border-dark-600">
									€{{ event.revenue_generated.toLocaleString() }}
								</td>
								<td class="py-3.5 px-3 text-center border-b border-primary-50 dark:border-dark-600">
									<span class="inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold" :class="[
										event.ticket_selling_status === 'active'
											? 'bg-primary-50 text-primary-600 dark:bg-primary-400/15 dark:text-primary-300'
											: event.status === 'sold_out'
												? 'bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400'
												: event.ticket_selling_status === 'selling'
													? 'bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400'
													: 'bg-dark-50 text-muted dark:bg-dark-600 dark:text-muted-light',
									]">
										{{ event.ticket_selling_status }}
									</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<!-- ── Pagination bar ── -->
				<div v-if="topEvents.length"
					class="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-primary-100 dark:border-dark-600">
					<span class="text-[12.5px] text-muted dark:text-muted-light">
						Showing
						<span class="font-bold text-dark-700 dark:text-white">{{ topEventsPaginationStart }}–{{
							topEventsPaginationEnd }}</span>
						of
						<span class="font-bold text-dark-700 dark:text-white">{{ topEvents.length }}</span>
					</span>

					<div
						class="inline-flex items-center gap-0.5 p-1 rounded-2xl bg-primary-50/70 dark:bg-dark-600/70 border border-primary-100 dark:border-dark-500">
						<button class="nav-btn" :disabled="topEventsPage === 1" @click="topEventsPage = 1"
							title="First page">
							<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
									d="M18 19l-7-7 7-7M11 19l-7-7 7-7" />
							</svg>
						</button>
						<button class="nav-btn" :disabled="topEventsPage === 1" @click="topEventsPage--"
							title="Previous page">
							<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
									d="M15 19l-7-7 7-7" />
							</svg>
						</button>

						<template v-for="(item, idx) in topEventsPageItems">
							<span v-if="item === '...'" :key="`dots-${idx}`"
								class="w-8 h-8 flex items-center justify-center text-[13px] text-muted dark:text-muted-light select-none">⋯</span>
							<button v-else :key="item" class="page-num-btn"
								:class="item === topEventsPage ? 'page-num-btn-active' : ''"
								@click="topEventsPage = item">
								{{ item }}
							</button>
						</template>

						<button class="nav-btn" :disabled="topEventsPage === topEventsTotalPages"
							@click="topEventsPage++" title="Next page">
							<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
									d="M9 5l7 7-7 7" />
							</svg>
						</button>
						<button class="nav-btn" :disabled="topEventsPage === topEventsTotalPages"
							@click="topEventsPage = topEventsTotalPages" title="Last page">
							<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
									d="M6 19l7-7-7-7M13 19l7-7-7-7" />
							</svg>
						</button>
					</div>
				</div>
			</div>

			<!-- Recent Activity -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card p-6 border border-primary-100 dark:border-dark-600 transition-colors duration-300">
				<h2 class="font-display text-[15px] font-bold text-dark-700 dark:text-white mb-5">
					Recent Activity
				</h2>

				<div v-if="!hasRecentActivity" class="h-40 flex flex-col items-center justify-center gap-2">
					<div class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center">
						<svg class="w-5 h-5 text-muted dark:text-muted-light" fill="none" stroke="currentColor"
							viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
								d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
						</svg>
					</div>
					<p class="text-[13px] text-muted dark:text-muted-light text-center">
						No recent activities yet.
					</p>
				</div>

				<div v-if="hasRecentActivity" class="space-y-4">
					<div v-for="activity in recentActivities" :key="activity.id" class="flex gap-3 group">
						<div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-200"
							:class="[
								activity.type === 'created'
									? 'bg-primary-50 dark:bg-primary-400/15'
									: activity.type === 'sold'
										? 'bg-blue-50 dark:bg-blue-950/30'
										: activity.type === 'updated'
											? 'bg-blue-50 dark:bg-blue-950/30'
											: activity.type === 'payment'
												? 'bg-primary-50 dark:bg-primary-400/15'
												: 'bg-red-50 dark:bg-red-950/30',
							]">
							<component :is="activity.icon" />
						</div>
						<div class="flex-1 min-w-0">
							<div class="text-[13px] font-semibold text-dark-700 dark:text-white">
								{{ activity.title }}
							</div>
							<div class="text-[13px] text-muted dark:text-muted-light truncate">
								{{ activity.description }}
							</div>
							<div class="text-[11px] text-muted dark:text-muted-light/70 mt-0.5">
								{{ activity.time }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>


<script>
import InfoCard from "@/components/InfoCard.vue";
import { onMounted, computed, ref, watch } from "vue";
import { useStore } from 'vuex';
import Statistics from '@/components/StatisticView.vue'
import Breadcrumbs from '@/components/Breadcrumbs.vue'

// const PlusCircleIcon = {
// 	template: `<svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`,
// };
// const TicketIcon = {
// 	template: `<svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>`,
// };
// const PencilIcon = {
// 	template: `<svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>`,
// };
// const CashIcon = {
// 	template: `<svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`,
// };

const recentActivities = ref([
	// {
	// 	id: 1,
	// 	type: "created",
	// 	title: "New event created",
	// 	description: "Winter Wonderland Concert",
	// 	time: "5 minutes ago",
	// 	icon: PlusCircleIcon,
	// },
	// {
	// 	id: 2,
	// 	type: "sold",
	// 	title: "Ticket sold",
	// 	description: "Summer Music Festival 2024",
	// 	time: "12 minutes ago",
	// 	icon: TicketIcon,
	// },
	// {
	// 	id: 3,
	// 	type: "updated",
	// 	title: "Event updated",
	// 	description: "Tech Conference Asia",
	// 	time: "1 hour ago",
	// 	icon: PencilIcon,
	// },
	// {
	// 	id: 4,
	// 	type: "payment",
	// 	title: "Payment received",
	// 	description: "Food & Wine Expo",
	// 	time: "2 hours ago",
	// 	icon: CashIcon,
	// },
]);

export default {
	components: { InfoCard, Statistics, Breadcrumbs },

	setup() {
		const store = useStore();

		onMounted(() => {
			store.dispatch("dashboard/fetchAnalytics");
		});

		const selectedPeriod = ref("Month");
		const selectedMetric = ref("sales_velocity");
		const kpis = computed(() => store.getters["dashboard/kpis"]);
		const charts = computed(() => store.getters["dashboard/charts"]);

		const eventCount = computed(() => kpis.value?.total_events_created ?? 0);
		const totalTicketsSold = computed(
			() => kpis.value?.total_tickets_sold ?? 0,
		);
		const activeEventCount = computed(() => kpis.value?.active_events ?? 0);
		const totalRevenue = computed(() => kpis.value?.revenue_generated ?? 0);
		const attendeesCount = computed(
			() => kpis.value?.total_attendees_registered ?? 0,
		);
		const completedEventCount = computed(
			() => kpis.value?.completed_events ?? 0,
		);
		const canceledEventCount = computed(() => kpis.value?.canceled_events ?? 0);
		const draftedEventCount = computed(() => kpis.value?.drafted_events ?? 0);

		const topEvents = computed(() => store.state.dashboard.topEvents);
		const soldOutEvents = computed(() => store.state.dashboard.soldOutEvents);
		const soldOutActiveEvents = computed(
			() => store.state.dashboard.soldOutActiveEvents,
		);
		const averageTicketPrice = computed(
			() => store.state.dashboard.averageTicketPrice,
		);
		const categoryCount = computed(() => store.state.dashboard.categoryCount);
		const eventTrend = computed(() => store.state.dashboard.eventTrend);

		const isLoadingEvents = computed(
			() => store.state.dashboard.loading.events,
		);
		const isLoadingCategories = computed(
			() => store.state.dashboard.loading.categories,
		);
		const isLoadingAttendants = computed(
			() => store.state.dashboard.loading.attendants,
		);

		const hasRecentActivity = computed(() => recentActivities.value.length > 0);

		const userInfo = computed(() => store.state.auth.user || {});
		const forAdmin = () => {
			if (!userInfo.value?.user_type) return false;
			return (
				Number(userInfo.value.user_type) !== 1 &&
				Number(userInfo.value.user_type) !== 2
			);
		};
		const forClient = () => {
			if (!userInfo.value?.user_type) return false;
			return (
				Number(userInfo.value.user_type) !== 1 &&
				Number(userInfo.value.user_type) === 2
			);
		};
		const topEventsPage = ref(1);
		const topEventsPageSize = ref(5);

		const topEventsTotalPages = computed(() =>
			Math.max(1, Math.ceil((topEvents.value?.length || 0) / topEventsPageSize.value))
		);

		const paginatedTopEvents = computed(() => {
			const page = Math.min(topEventsPage.value, topEventsTotalPages.value);
			const start = (page - 1) * topEventsPageSize.value;
			return (topEvents.value || []).slice(start, start + topEventsPageSize.value);
		});

		const topEventsPaginationStart = computed(() => {
			if (!topEvents.value?.length) return 0;
			return (Math.min(topEventsPage.value, topEventsTotalPages.value) - 1) * topEventsPageSize.value + 1;
		});

		const topEventsPaginationEnd = computed(() =>
			Math.min(topEventsPaginationStart.value + topEventsPageSize.value - 1, topEvents.value?.length || 0)
		);

		// Page numbers with ellipsis, e.g. 1 … 4 5 [6] 7 8 … 20
		const topEventsPageItems = computed(() => {
			const total = topEventsTotalPages.value;
			const current = Math.min(topEventsPage.value, total);
			const delta = 1;
			const range = [];
			for (let i = 1; i <= total; i++) {
				if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
					range.push(i);
				}
			}
			const withDots = [];
			let last = null;
			range.forEach((i) => {
				if (last !== null) {
					if (i - last === 2) withDots.push(last + 1);
					else if (i - last > 2) withDots.push("...");
				}
				withDots.push(i);
				last = i;
			});
			return withDots;
		});

		// topEvents comes from the store and can change size on refetch — don't
		// leave the user stranded on a page that no longer exists.
		watch(topEvents, () => {
			topEventsPage.value = 1;
		});

		onMounted(async () => {
			await Promise.all([
				store.dispatch("dashboard/fetchAnalytics"),
				store.dispatch("dashboard/fetchEvents"),
				store.dispatch("dashboard/fetchCategories"),
				store.dispatch("dashboard/fetchAttendees"),
			]);
		});

		return {
			eventCount,
			activeEventCount,
			completedEventCount,
			canceledEventCount,
			draftedEventCount,
			soldOutEvents,
			soldOutActiveEvents,
			averageTicketPrice,
			categoryCount,
			attendeesCount,
			eventTrend,
			isLoadingEvents,
			isLoadingCategories,
			isLoadingAttendants,
			forAdmin,
			forClient,
			selectedPeriod,
			topEvents,
			recentActivities,
			hasRecentActivity,
			totalTicketsSold,
			totalRevenue,
			charts,
			selectedMetric,
			// pagination
			topEventsPage,
			topEventsPageSize,
			topEventsTotalPages,
			paginatedTopEvents,
			topEventsPaginationStart,
			topEventsPaginationEnd,
			topEventsPageItems,

		};
	},
};
</script>
<style scoped>
/* Prev/next/first/last icon buttons */
.nav-btn {
	@apply flex items-center justify-center w-8 h-8 rounded-xl text-muted dark:text-muted-light hover:bg-surface dark:hover:bg-dark-700 hover:text-primary-600 dark:hover:text-primary-300 hover:shadow-sm disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-muted disabled:cursor-not-allowed transition-all duration-150;
}

/* Numbered page buttons */
.page-num-btn {
	@apply flex items-center justify-center min-w-[32px] h-8 px-1.5 rounded-xl text-[12.5px] font-bold text-dark-700 dark:text-white hover:bg-surface dark:hover:bg-dark-700 hover:text-primary-600 dark:hover:text-primary-300 transition-all duration-150;
}

.page-num-btn-active {
	@apply bg-primary-400 text-white shadow-teal hover:bg-primary-500 hover:text-white dark:hover:bg-primary-400;
}
</style>